import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactNode } from 'react'
import React, { useEffect, useState } from 'react'
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import { clearAllCaches } from '../util/serviceWorkerUtils'

function UpdateGuard({ children }: { children: ReactNode }) {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [skipUpdate, setSkipUpdate] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  if (!('serviceWorker' in navigator)) {
    return <>{children}</>
  }

  useEffect(() => {
    // Check for service worker updates
    const checkForUpdate = async () => {
      const registration = await navigator.serviceWorker.getRegistration()
      if (registration) {
        // Check if there's already an update waiting
        if (registration.waiting) {
          setUpdateAvailable(true)
        }
        
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true)
              }
            })
          }
        })
      }
    }

    checkForUpdate()
  }, [])

  const handleReload = async () => {
    setIsUpdating(true)
    
    try {
      // Use our utility function to clear all caches
      await clearAllCaches()
      
      // Then activate the waiting service worker
      const registration = await navigator.serviceWorker.getRegistration()
      if (registration && registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      }
      else {
        // If no waiting worker, force reload
        window.location.reload()
      }
    }
    catch (error) {
      console.error('Error during update:', error)
      // Force reload as fallback
      window.location.reload()
    }
  }

  const handleClose = () => {
    setSkipUpdate(true)
  }

  useEffect(() => {
    let refreshing = false
    const onControllerChange = () => {
      // Prevent multiple reloads
      if (refreshing)
        return
      refreshing = true
      window.location.reload()
    }

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
    }
  }, [])

  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      if (event.data?.type === 'NEW_VERSION_ACTIVATED') {
        window.location.reload()
      }
    }

    navigator.serviceWorker.addEventListener('message', messageHandler)
    return () => {
      navigator.serviceWorker.removeEventListener('message', messageHandler)
    }
  }, [])

  return (
    <div>
      {updateAvailable && !skipUpdate && (
        <div
          className="dark-mode"
          style={{
            width: '100vw',
            height: '100vh',
            position: 'fixed',
            top: '0',
            left: '0',
            zIndex: 9999,
          }}
        >
          <div className="">
            <div className="pt-3" />
            <section className="content m-3">
              <div className="card card-primary">
                <div className="card-header">
                  <h3 className="card-title">Update beschikbaar</h3>
                  <button className="close" onClick={handleClose}>
                    <span aria-hidden="true"><FontAwesomeIcon icon={faXmark} /></span>
                  </button>
                </div>
                <div className="card-body">
                  <div className="">
                    <div className="row">
                      <div className="col-1">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle as IconProp}
                          size="2x"
                          className="text-danger"
                          style={{
                            position: 'relative',
                            top: '50%',
                            transform: 'translatey(-50%)',
                          }}
                        />
                      </div>
                      <div
                        className="col-11"
                        style={{
                          paddingLeft: '20px',
                        }}
                      >
                        <h3 className="text-danger">Update beschikbaar</h3>
                        <p
                          className="text-danger"
                          style={{
                            margin: '0',
                          }}
                        >
                          Er is een update beschikbaar. Voer nu een update uit.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-footer">
                  <button
                    className="btn btn-primary"
                    onClick={handleReload}
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Bezig met updaten...' : 'Update uitvoeren'}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
      {children}
    </div>
  )
}

export default UpdateGuard
